import type { RewardedPlacement } from '@/api/types';
import { useAdOverlay } from './overlay';
import type { AdProvider, CardPlacement, DisplayHandle, InterstitialResult, RewardedResult } from './types';

export interface FakeAdOptions {
  /** Scripted outcomes (Vitest). */
  rewarded?: RewardedResult;
  interstitial?: InterstitialResult;
  /** Cards render a placeholder (true) or come back empty (false). */
  display?: boolean;
  delayMs?: number;
  /** Throw from every method — to prove callers never block on a broken provider. */
  broken?: boolean;
  /**
   * Test mode in the app (GPT couldn't load): draw neutral placeholder
   * creatives the person can watch or close, instead of scripted results.
   */
  interactive?: boolean;
  /** Interactive rewarded length; ≥ the server's min_watch_s (15 s). */
  rewardedSeconds?: number;
}

export interface FakeAdCalls {
  rewarded: RewardedPlacement[];
  interstitial: string[];
  display: CardPlacement[];
  destroyAll: number;
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Mockable provider: Vitest, and `ads.test_mode` when gpt.js fails to load. Never a real brand. */
export class FakeAdProvider implements AdProvider {
  readonly kind = 'fake' as const;
  readonly calls: FakeAdCalls = { rewarded: [], interstitial: [], display: [], destroyAll: 0 };

  constructor(readonly opts: FakeAdOptions = {}) {}

  private async pause() {
    if (this.opts.broken) throw new Error('fake ad provider is broken');
    if (this.opts.delayMs) await wait(this.opts.delayMs);
  }

  async showRewarded(placement: RewardedPlacement): Promise<RewardedResult> {
    this.calls.rewarded.push(placement);
    await this.pause();
    if (this.opts.interactive) {
      const seconds = this.opts.rewardedSeconds ?? 16;
      return new Promise((resolve) =>
        useAdOverlay.getState().set({
          kind: 'fake-rewarded',
          placement,
          seconds,
          resolve: (r) => {
            useAdOverlay.getState().set(null);
            resolve(r);
          },
        }),
      );
    }
    return this.opts.rewarded ?? 'granted';
  }

  async showInterstitial(placement: 'between_calls'): Promise<InterstitialResult> {
    this.calls.interstitial.push(placement);
    await this.pause();
    if (this.opts.interactive) {
      return new Promise((resolve) =>
        useAdOverlay.getState().set({
          kind: 'fake-interstitial',
          seconds: 5,
          resolve: () => resolve('shown'),
        }),
      );
    }
    return this.opts.interstitial ?? 'shown';
  }

  async renderDisplay(placement: CardPlacement): Promise<DisplayHandle> {
    this.calls.display.push(placement);
    await this.pause();
    const filled = this.opts.display ?? true;
    return { filled, placeholder: filled, destroy: () => undefined };
  }

  destroyAll(): void {
    this.calls.destroyAll += 1;
  }
}
